import { useCallback, useEffect, useRef, useState } from 'react';
import type { DashboardSnapshot, SectionPatch, ViewKey, WSServerMessage } from '@dash/shared';
import { buildWsUrl } from '../lib/ws.ts';

type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'offline';

interface UseWsResult {
  snapshot: DashboardSnapshot | null;
  state: ConnectionState;
  lastUpdate: Date | null;
  alert: { severity: 'info' | 'warning' | 'critical'; text: string; timestamp: string } | null;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_CAP_MS = 30_000;

/**
 * WS client com auto-reconexao exponencial, ping/pong e merge de patches.
 * Os patches recebidos sao aplicados sobre o ultimo snapshot - nunca substituem cegamente.
 */
export function useWebSocket(view: ViewKey, token?: string): UseWsResult {
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [state, setState] = useState<ConnectionState>('connecting');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [alert, setAlert] = useState<UseWsResult['alert']>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedByUs = useRef(false);
  const pingTimer = useRef<number | null>(null);

  const connect = useCallback(() => {
    if (closedByUs.current) return;
    setState((s) => (s === 'open' ? 'reconnecting' : 'connecting'));
    const ws = new WebSocket(buildWsUrl(view, token));
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setState('open');
      // ping a cada 20s
      if (pingTimer.current) window.clearInterval(pingTimer.current);
      pingTimer.current = window.setInterval(() => {
        if (ws.readyState === ws.OPEN) ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
      }, 20_000);
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as WSServerMessage;
        if (msg.type === 'snapshot') {
          setSnapshot(msg.payload);
          setLastUpdate(new Date());
        } else if (msg.type === 'patch') {
          setSnapshot((prev) => (prev ? applyPatch(prev, msg.patch) : prev));
          setLastUpdate(new Date());
        } else if (msg.type === 'alert') {
          setAlert({ severity: msg.severity, text: msg.text, timestamp: msg.timestamp });
          window.setTimeout(() => setAlert(null), 12_000);
        }
      } catch (err) {
        console.error('ws parse error', err);
      }
    };

    ws.onclose = () => {
      if (pingTimer.current) {
        window.clearInterval(pingTimer.current);
        pingTimer.current = null;
      }
      if (closedByUs.current) return;
      attemptRef.current += 1;
      const backoff = Math.min(RECONNECT_BASE_MS * 2 ** (attemptRef.current - 1), RECONNECT_CAP_MS);
      const jitter = Math.random() * 0.3 * backoff;
      setState(attemptRef.current > 3 ? 'offline' : 'reconnecting');
      window.setTimeout(connect, backoff + jitter);
    };

    ws.onerror = () => {
      // onclose ira disparar e cuidar do reconnect
    };
  }, [view, token]);

  useEffect(() => {
    closedByUs.current = false;
    connect();
    return () => {
      closedByUs.current = true;
      if (pingTimer.current) window.clearInterval(pingTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { snapshot, state, lastUpdate, alert };
}

function applyPatch(snap: DashboardSnapshot, patch: SectionPatch): DashboardSnapshot {
  switch (patch.section) {
    case 'rede':
      return { ...snap, rede: { ...snap.rede, ...patch.payload }, timestamp: new Date().toISOString() };
    case 'atendimento':
      return {
        ...snap,
        atendimento: {
          ...snap.atendimento,
          ...patch.payload,
          osAbertas: { ...snap.atendimento.osAbertas, ...(patch.payload.osAbertas ?? {}) },
          pabx: { ...snap.atendimento.pabx, ...(patch.payload.pabx ?? {}) },
        },
        timestamp: new Date().toISOString(),
      };
    case 'negocio':
      return { ...snap, negocio: { ...snap.negocio, ...patch.payload }, timestamp: new Date().toISOString() };
    case 'avisos':
      return { ...snap, avisos: patch.payload.avisos, timestamp: new Date().toISOString() };
  }
}
